import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  signal,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { MenuItem, PrimeIcons } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogService } from 'primeng/dynamicdialog';
import { SpeedDialModule } from 'primeng/speeddial';

import {
  VideoClipFormControl,
  VideoProjectFormService,
} from '../video-project.form';
import { VideoShotAssetComponent } from '../video-shot-asset/video-shot-asset.component';
import {
  VideoClipEditorComponent,
  VideoClipEditorFooterComponent,
} from './video-clip-editor/video-clip-editor.component';

@Component({
  selector: 'app-video-clip',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    SpeedDialModule,
    VideoShotAssetComponent,
  ],
  providers: [DialogService],
  templateUrl: './video-clip.component.html',
  styleUrl: './video-clip.component.scss',
})
export class VideoClipComponent implements OnInit {
  @Input() hidden = false;
  @Input() clip: FormGroup<VideoClipFormControl>;

  @Output() clipDelete = new EventEmitter();
  @Output() clipReset = new EventEmitter();

  actions: MenuItem[] = [
    {
      icon: PrimeIcons.PEN_TO_SQUARE,
      command: () => {
        this.edit();
      },
    },
    {
      icon: PrimeIcons.TRASH,
      command: () => {
        this.clipDelete.emit();
      },
    },
    {
      icon: PrimeIcons.UNDO,
      command: () => {
        this.reset();
      },
    },
  ];

  get filteredActions() {
    return this.hasChanges()
      ? this.actions
      : this.actions.filter(({ icon }) => {
          return icon !== PrimeIcons.UNDO;
        });
  }

  hasChanges: WritableSignal<boolean> = signal(false);

  constructor(
    private readonly destroyRef: DestroyRef,
    private readonly dialogService: DialogService,
    private readonly videoProjectFormService: VideoProjectFormService,
  ) {}

  ngOnInit() {
    this.videoProjectFormService
      .$onClipChanges(this.clip)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([hasChanges]) => {
        this.hasChanges.set(hasChanges);
      });
  }

  private edit() {
    const ref = this.dialogService.open(VideoClipEditorComponent, {
      header: 'Video Clip Editor',
      modal: true,
      draggable: false,
      resizable: false,
      maximizable: false,
      closeOnEscape: true,
      dismissableMask: true,
      closable: true,
      styleClass: 'w-2/4',
      data: {
        clip: this.clip,
        hasChanges: this.hasChanges,
      },
      templates: {
        footer: VideoClipEditorFooterComponent,
      },
    });

    ref.onClose.subscribe(action => {
      switch (action) {
        case 'reset':
          this.reset();
          break;
        default:
          break;
      }
    });
  }

  private reset() {
    const initialValue = this.videoProjectFormService.getClipInitialValue(
      this.clip,
    );
    if (initialValue) {
      this.clip.reset(initialValue);
    }
  }

  onDeleteClick() {
    this.clipDelete.emit();
  }
}
